import {Component, OnInit, EventEmitter, Output, ViewChild, ElementRef, NgZone, TemplateRef, Input} from '@angular/core';
import {RoutesService} from '../../core/routes/routes.service';
import {AccountsService} from '../../core/accounts/accounts.service';
import {PlacesService} from '../../core/places/places.service';
import {UtilsService} from '../../core/utils/utils.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BsModalService, TabsetComponent} from 'ngx-bootstrap';
import {NavigationService} from '../../core/navigation/navigation.service';

@Component({
  selector: 'app-routes',
  templateUrl: './routes.component.html',
  styleUrls: ['./routes.component.scss'],
  host: {
    '(document:mousedown)': 'onClick($event)'
  }
})
export class RoutesComponent implements OnInit {

  @Input() load: boolean;

  @Output() close = new EventEmitter();

  @ViewChild('confirmTemplate') confirmTemplate: TemplateRef<any>;

  @ViewChild('tabs') staticTabs: TabsetComponent;

  @ViewChild('popover') popover;

  @ViewChild('locationPopover') locationPopover;

  routes: any[];

  accountsForRoute: any[];

  routeToAdd: any;

  route: any;

  sortOptions = {
    animation: 150,
    handle: '.handle',
    onStart: () => {
      this.routesService.clearLegs();
    },
    onEnd: () => {
      this.routesService.getRoute();
    }
  };

  showLocationPopover: boolean;
  showCreateRoutePopover: boolean;
  showMorePopover: boolean;

  placeToAdd: any;

  routeForm: FormGroup;

  modalRef: any;

  bsConfig = {
    containerClass: 'theme-blue'
  };

  constructor(private zone: NgZone,
              private fb: FormBuilder,
              private navigationService: NavigationService,
              private modalService: BsModalService,
              private utilsService: UtilsService,
              private placesService: PlacesService,
              private accountsService: AccountsService,
              private routesService: RoutesService) {
    this.route = this.routesService.route;
  }

  ngOnInit() {
    this.routesService.list()
      .subscribe((routes: any[]) => {
        this.routes = routes;
        if (this.routes.length && this.load) {
          this.get(null, this.routes[0]);
        }
      });

    this.routeForm = this.fb.group({
      name: ['', Validators.required],
      date: [new Date(), Validators.required]
    });

    this.routesService.confirmRoute.subscribe((accounts: any[]) => {
      if (accounts) {
        if (this.routesService.saved) {
          this.routesService.clear();
          this.routesService.bulkAddAccounts(accounts);
          this.navigationService.navigateToRoute();
        } else {
          this.accountsForRoute = accounts;
          this.openConfirmModal();
        }
      }
    });

    this.modalService.onHide.subscribe(() => {
      this.accountsForRoute = null;
      this.routeToAdd = null;
    });
  }

  private onClick(event): void {
    if (this.showMorePopover
        && (
        !this.popover._popover
        || !this.popover._popover._componentRef
        || !this.popover._popover._componentRef.location
        || !this.popover._popover._componentRef.location.nativeElement
        || !this.popover._popover._componentRef.location.nativeElement.contains(event.target)
      )
    ) {
      this.hidePopover();
    }
  }

  hidePopover() {
    this.showMorePopover = false;
    this.popover.hide();
  }

  addNewPlace(place) {
    if (place) {
      this.routesService.addPlaceToRoute(place);
    }
    this.showLocationPopover = false;
  }

  clear() {
    if (this.routesService.saved) {
      this.routesService.clear();
      this.resetRouteForm();
      this.navigationService.navigateToLasso();
    } else {
      this.openConfirmModal();
    }
  }

  get(e, route) {
    this.routesService.get(route._id)
      .subscribe(detailedRoute => {
        if (this.routesService.saved) {
          this.routesService.clear();
          this.routesService.setRoute(detailedRoute);
          this.resetRouteForm(detailedRoute);
          this.routesService.getRoute();
          this.routesService.saved = true;
          this.staticTabs.tabs[0].active = true;
        } else {
          this.routeToAdd = detailedRoute;
          this.openConfirmModal();
        }
      }, err => {
        alert(err.error.message || 'An error occurred');
      });
    if (e) {
      e.preventDefault();
    }

  }

  delete(e, route) {
    const id = route._id;
    this.routesService.delete(id)
      .subscribe(() => {
        this.routes = this.routes.filter(r => r._id !== id);
      });
    e.stopPropagation();
  }

  exportToPdf() {
    this.showMorePopover = false;
    this.routesService.exportToPdf(this.route)
      .subscribe((data: any) => {
        window.location.assign(`/api/download/${data.filename}`);
      }, err => {
        alert(err.error.message || 'An error occurred');
      });
  }

  exportToCalendar() {
    this.showMorePopover = false;
    this.routesService.exportToCalendar(this.route)
      .subscribe((data: any) => {
        window.location.assign(`/api/download/${data.filename}`);
      }, err => {
        alert(err.error.message || 'An error occurred');
      });
  }

  getDayTotal() {
    return this.utilsService.getHrsMinFromMinutes(this.routesService.getDayTotal());
  }

  getTravelTime() {
    return  this.utilsService.getHrsMinFromMinutes(this.routesService.getTravelTime());
  }

  getDistance() {
    return this.routesService.getDistance();
  }

  getMeetingTime() {
    return this.utilsService.getHrsMinFromMinutes(this.routesService.getMeetingTime());
  }

  optimizeEnabled() {
    return this.routesService.getAllPoints().length > 3 && !this.route.optimized;
  }

  processChanges() {
    this.routesService.calculateTimes();
  }

  optimize() {
    this.routesService.getRoute(true);
  }

  save(routeData) {
    const route = Object.assign({}, this.route, routeData);
    this.routesService.create(route)
      .subscribe((newRoute: any) => {
        this.routesService.setRoute(newRoute);

        const updatedRoute = this.routes.find(r => r._id === newRoute._id);

        if (updatedRoute) {
          this.routes = this.routes.filter(r => {
            if (r._id === newRoute._id) {
              return newRoute;
            } else {
              return r;
            }
          });
        } else {
          this.routes = [newRoute, ...this.routes];
        }

        this.resetRouteForm(newRoute);

        this.routesService.saved = true;

      }, (err) => {
        alert(err.error.message || 'An error occurred');
      });
    this.showCreateRoutePopover = false;
  }

  resetRouteForm(route?) {
    this.routeForm.reset({name: route && route.name || '', date: route && new Date(route.date) || new Date()});
  }

  openConfirmModal() {
    this.modalRef = this.modalService.show(this.confirmTemplate);
  }

  confirm() {
    if (this.accountsForRoute && this.accountsForRoute.length) {
      this.routesService.clear();
      this.routesService.bulkAddAccounts(this.accountsForRoute);
      this.staticTabs.tabs[0].active = true;
      this.navigationService.navigateToRoute();
    } else if (this.routeToAdd) {
      this.routesService.clear();
      this.routesService.setRoute(this.routeToAdd);
      this.resetRouteForm(this.routeToAdd);
      this.routesService.getRoute();
      this.routesService.saved = true;
      this.staticTabs.tabs[0].active = true;
    } else {
      this.routesService.clear();
      this.resetRouteForm();
    }

    this.modalRef.hide();
    this.navigationService.navigateToLasso();
  }

  cancel() {
    this.modalRef.hide();
  }

}



// WEBPACK FOOTER //
// ./client/app/components/routes/routes.component.ts